<template>
  <div class="tw:inline-flex">
    <img
      v-if="src && !skeleton"
      :src="src"
      alt=""
      :class="['tw:rounded-full tw:object-cover', skeleton && 'skeleton-play']"
      :style="dimensionStyle"
    />
    <div
      v-else
      :class="['tw:flex tw:items-center tw:justify-center tw:rounded-full', skeleton && 'skeleton-play']"
      :style="placeholderStyle"
    >
      <TextPlay
        v-if="!skeleton"
        color="white"
        weight="bold"
        :text="initial"
        :style="{ fontSize: initialFontSize }"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import TextPlay from '../TextPlay/TextPlay.vue'

const props = withDefaults(defineProps<{
  src?: string
  size?: 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl'
  customSize?: string
  customBgColor?: string
  name?: string
  skeleton?: boolean
}>(), {
  src: undefined,
  size: 'xs',
  customSize: undefined,
  customBgColor: undefined,
  name: '',
  skeleton: false,
})

const SIZE_MAP: Record<string, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
}

const COLORS = [
  'var(--feedback-error-500)',
  'var(--primary-500)',
  'var(--feedback-warning-500)',
  'var(--feedback-success-500)',
]

// Color based on first letter of name — deterministic, same letter always gets same color
const stableColor = computed(() => {
  if (!props.name) return COLORS[0]
  const firstChar = props.name.trim()[0]?.toUpperCase() ?? ''
  const charCode = firstChar.charCodeAt(0) || 0
  return COLORS[charCode % COLORS.length]
})

const resolvedSize = computed(() => props.customSize ?? SIZE_MAP[props.size] ?? '0.75rem')

const initial = computed(() => {
  if (!props.name) return ''
  const words = props.name.trim().split(/\s+/)
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase()
  }
  return words[0][0]?.toUpperCase() ?? ''
})

const initialFontSize = computed(() => `calc(${resolvedSize.value} / 2.2)`)

const dimensionStyle = computed(() => ({
  width: resolvedSize.value,
  height: resolvedSize.value,
}))

const placeholderStyle = computed(() => ({
  width: resolvedSize.value,
  height: resolvedSize.value,
  backgroundColor: props.src ? 'transparent' : (props.customBgColor ?? stableColor.value),
}))
</script>
